import React, { useState, useEffect, useRef } from "react";
import { OptionDropDown } from "./optionDropDown";
import { Input } from "src/shadcn/ui/input";
import { X, ChevronDown } from "lucide-react";

interface DropdownFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options?: string[];
  placeholder?: string;
}

export const DropdownField: React.FC<DropdownFieldProps> = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select..."
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    setIsOpen(true);
    if (!newValue) {
      onChange(undefined);
    }
  };

  const handleSelect = (option: string) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange(undefined);
    setIsOpen(false);
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">{label}</label>
      <div className="relative" ref={containerRef}>
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={placeholder}
          className="pr-20 text-sm h-9"
        />
        <div className="absolute right-1 top-1 flex gap-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-secondary-100 rounded transition-colors"
            >
              <X className="h-3.5 w-3.5 text-secondary-500" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 hover:bg-secondary-100 rounded transition-colors"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
        <OptionDropDown
          value={inputValue}
          onChange={handleSelect}
          isOpen={isOpen}
          options={options}
          triggerRef={containerRef}
        />
      </div>
    </div>
  );
};
